import { useMemo, useState } from 'react';
import _ from 'lodash';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { enqueueSnackbar } from 'notistack';
import {
  addUpdate,
  addReply,
  addFileToUpdate,
  getBoardItemUpdates,
  getBoardItemsByIds,
  getMe,
  type BoardColumn,
  type BoardItem,
  type BoardRelationColumnValue,
} from '@gorilla/common/src/lib/monday-api/api';
import { parseJson } from '@gorilla/common/src/lib/engine/converters';
import { useItem } from '../shared/hooks/use-item';
import { useBoard } from '../shared/hooks/use-boards';
import { boardItemUpdatesToPortalComments, addClientNameToComments } from '@gorilla/portal-shared/src/comments';
import { monday, mondayClient } from '../services/monday';
import { createActivity, triggerCommentAutomation } from '../services/portal';
import { usePortals } from '../hooks/use-portals';
import { extractErrorMessage } from '../lib/utils';
import { type MondayContext } from './use-client';

type ItemCommentsManagerInitState = { state: 'init' };
type ItemCommentsManagerReadyState = { state: 'ready' };
type ItemCommentsManagerErrorState = {
  state: 'error';
  errorKind: 'MONDAY_CONTEXT' | 'MISSING_PERMISSIONS' | 'ME' | 'LOAD_PORTALS';
  error: string;
};
type ItemCommentsManagerState = ItemCommentsManagerInitState | ItemCommentsManagerReadyState | ItemCommentsManagerErrorState;

const GLOBAL_SELECTION_KEY = 'cpb-item-comments-manager--last-selection';
const itemSelectionKey = (itemId: number) => `cpb-item-comments-manager-${itemId}--last-selection`;

type LastSelection = { portalId?: string; clientItemId?: number };

function parseSelection(raw: string | null): LastSelection {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as LastSelection;
  } catch {
    return {};
  }
}

// item-level first, then global fallback
function readLastSelection(itemId?: number): LastSelection {
  if (itemId) {
    const itemVal = parseSelection(window.localStorage.getItem(itemSelectionKey(itemId)));
    if (itemVal.portalId) return itemVal;
  }
  // Global fallback: pre-select only the last-used portal. The remembered client belongs to a
  // specific item's context, so it must not be pre-selected for an item that has no stored selection.
  const global = parseSelection(window.localStorage.getItem(GLOBAL_SELECTION_KEY));
  return { portalId: global.portalId };
}

// write both item-level and global
function writeLastSelection(itemId: number | undefined, value: LastSelection) {
  const raw = JSON.stringify(value);
  try {
    if (itemId) window.localStorage.setItem(itemSelectionKey(itemId), raw);
    window.localStorage.setItem(GLOBAL_SELECTION_KEY, raw);
  } catch {
    /* ignore quota/availability errors */
  }
}

// The items the open item links to through connect-boards columns that can point at the portal's
// client board. A column may connect several boards, so these aren't necessarily clients yet.
function findLinkedItemIds(item: BoardItem | null | undefined, columns: BoardColumn[] | undefined, clientBoardId: number | undefined) {
  if (!item || !columns || !clientBoardId) return [];

  const relationColumnIds = columns
    .filter((column) => column.type === 'board_relation')
    .filter((column) => {
      const settings = parseJson(column.settings_str);
      return Array.isArray(settings?.boardIds) && settings.boardIds.some((boardId: number | string) => +boardId === clientBoardId);
    })
    .map((column) => column.id);

  const linkedItemIds = _.uniq(
    item.column_values
      .filter((columnValue) => relationColumnIds.includes(columnValue.id))
      .flatMap((columnValue) => (columnValue as BoardRelationColumnValue).linked_item_ids ?? []),
  );

  // monday accepts at most 100 ids per items query
  return _.take(linkedItemIds, 100).map(Number);
}

// The first linked item that's really on the client board, in linked order. With several linked
// clients the first one wins; the user can still switch.
async function resolveLinkedClientItemId(linkedItemIds: number[], clientBoardId: number) {
  const items = (await getBoardItemsByIds(mondayClient, linkedItemIds, 'board { id }', {
    withoutBoardRelationValue: true,
    withoutMirrorValue: true,
  })) as (BoardItem & { board: { id: string } | null })[];
  const clientItemIds = items.filter((item) => item.board && +item.board.id === clientBoardId).map((item) => item.id);

  return linkedItemIds.find((itemId) => clientItemIds.includes(itemId));
}

export function useItemCommentsManager() {
  const pageSize = 100;
  const [userSelectedClientItemId, setUserSelectedClientItemId] = useState<number>();
  const [userSelectedPortalId, setUserSelectedPortalId] = useState<string>();
  const [page, setPage] = useState(1);
  const { portalsWithBoardInfo, portalsError, arePortalsLoading } = usePortals();

  const { data: mondayContext, error: mondayContextError } = useSWR(
    `/monday-context`,
    async () => {
      const mContext = ((await monday.get('context')) as any).data as MondayContext;

      if (!mContext) {
        throw new Error('This app can only be used inside of monday.com');
      }

      return mContext;
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const { data: currentUser, error: currentUserError } = useSWR(
    '/me',
    async () => {
      return getMe(mondayClient);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  // The effective selection is derived, not stored: an explicit user choice takes
  // precedence, otherwise we fall back to the last-remembered selection (item-level, then
  // global), otherwise the first portal. Reading is synchronous so no effect is needed —
  // it just re-derives once the async monday context / portal list arrive.
  const rememberedSelection = useMemo(() => readLastSelection(mondayContext?.itemId), [mondayContext?.itemId]);
  const rememberedPortalIsValid =
    !!rememberedSelection.portalId && portalsWithBoardInfo.some((portal) => portal.portalId === rememberedSelection.portalId);
  const userPortalIsValid = !!userSelectedPortalId && portalsWithBoardInfo.some((portal) => portal.portalId === userSelectedPortalId);

  const selectedPortalId = userPortalIsValid
    ? userSelectedPortalId
    : rememberedPortalIsValid
      ? rememberedSelection.portalId
      : portalsWithBoardInfo[0]?.portalId;

  // Inherit the remembered client only while the user hasn't touched the portal picker;
  // once they pick a portal the client resets and must be chosen for that portal's board.
  const rememberedClientItemId =
    userSelectedPortalId === undefined && rememberedPortalIsValid ? rememberedSelection.clientItemId : undefined;

  const selectedPortal = portalsWithBoardInfo.find((portal) => portal.portalId === selectedPortalId);

  const { item: currentItem } = useItem(mondayContext?.itemId);
  const { board: currentBoard } = useBoard(mondayContext?.boardId);
  const clientBoardId = selectedPortal?.boardId ? +selectedPortal.boardId : undefined;
  const linkedItemIds = findLinkedItemIds(currentItem, currentBoard?.columns, clientBoardId);

  const { data: linkedClientItemId } = useSWR(
    clientBoardId && linkedItemIds.length ? `/linked-client/${clientBoardId}/${linkedItemIds.join(',')}` : null,
    () => resolveLinkedClientItemId(linkedItemIds, clientBoardId!),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  // Last fallback: the client the open item is connected to on the portal's client board.
  const selectedClientItemId = userSelectedClientItemId ?? rememberedClientItemId ?? linkedClientItemId;

  const setSelectedPortalId = (portalId: string) => {
    setUserSelectedPortalId(portalId);
    setUserSelectedClientItemId(undefined);
    writeLastSelection(mondayContext?.itemId, { portalId, clientItemId: undefined });
  };

  const setSelectedClientItemId = (clientItemId: number | undefined) => {
    setUserSelectedClientItemId(clientItemId);
    writeLastSelection(mondayContext?.itemId, { portalId: selectedPortalId, clientItemId });
  };

  const { item: selectedClientItem } = useItem(selectedClientItemId);

  const {
    data: comments,
    mutate,
    isLoading: isLoadingComments,
    error: commentsError,
  } = useSWR(
    mondayContext?.itemId && selectedClientItem ? `/item-updates/${mondayContext?.itemId}/${page}/${selectedClientItem.id}` : null,
    async () => {
      const comments = await boardItemUpdatesToPortalComments(
        await getBoardItemUpdates(mondayClient, mondayContext?.itemId!, page, pageSize),
        selectedClientItemId!,
        'only-client',
      );

      if (selectedClientItem) {
        await addClientNameToComments(mondayClient, comments, selectedClientItem);
      }

      return comments;
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const {
    trigger: addUpdateTrigger,
    isMutating: isCreatingUpdate,
    error: creatingUpdateError,
  } = useSWRMutation(
    '/add-update',
    async (
      _url,
      {
        arg,
      }: {
        arg: {
          itemId: number;
          clientItemId: number;
          type: 'from' | 'to';
          body: string;
          files: File[];
          portalId: string;
          activityMessage: string;
        };
      },
    ) => {
      try {
        const updateId = await addUpdate(mondayClient, arg.itemId, `[${arg.type}-client:${arg.clientItemId}]\n\n${arg.body}`);

        try {
          if (arg.files) {
            for (const file of arg.files) {
              // TODO: handle uploading errors
              await addFileToUpdate(mondayClient, +updateId, file);
            }
          }
        } catch (err) {
          console.log(`Failed to upload some of the attachements`, err);
          // TODO: handle uploading errors
          enqueueSnackbar(`Failed to upload some of the attachements`, { variant: 'error' });
        }

        await Promise.all([
          createActivity(arg.portalId, arg.clientItemId, arg.activityMessage, arg.itemId).catch((err) => {
            console.log(`Failed to create activity`, err);
            enqueueSnackbar(`Failed to create activity. Please check if the Portal Manager has the permissions to create item updates.`, {
              variant: 'error',
            });
          }),
          triggerCommentAutomation({
            clientItemId: arg.clientItemId,
            itemId: arg.itemId,
            portalId: arg.portalId,
            comment: arg.body,
          }).catch((err) => {
            console.log(`Failed to trigger comment automation`, err);
            enqueueSnackbar(`Failed to trigger comment automation`, { variant: 'error' });
          }),
        ]);

        mutate();
        enqueueSnackbar('Comment added successfully', { variant: 'success' });

        return updateId;
      } catch (err) {
        console.log(`Failed to add comment`, err);
        enqueueSnackbar('Failed to add comment', { variant: 'error' });
        throw err;
      }
    },
  );

  const {
    trigger: addReplyTrigger,
    isMutating: isCreatingReply,
    error: creatingReplyError,
  } = useSWRMutation(
    '/add-reply',
    async (
      _url,
      {
        arg,
      }: {
        arg: {
          itemId: number;
          clientItemId: number;
          parentId: number;
          type: 'from' | 'to';
          body: string;
          portalId: string;
          activityMessage: string;
        };
      },
    ) => {
      try {
        const result = await addReply(
          mondayClient,
          arg.itemId,
          `${arg.parentId}`,
          `[${arg.type}-client:${arg.clientItemId}]\n\n${arg.body}`,
        );

        await Promise.all([
          createActivity(arg.portalId, arg.clientItemId, arg.activityMessage, arg.itemId).catch((err) => {
            console.log(`Failed to create activity`, err);
            enqueueSnackbar(`Failed to create activity. Please check if the Portal Manager has the permissions to create item updates.`, {
              variant: 'error',
            });
          }),
          triggerCommentAutomation({
            clientItemId: arg.clientItemId,
            itemId: arg.itemId,
            portalId: arg.portalId,
            comment: arg.body,
          }).catch((err) => {
            console.log(`Failed to trigger comment automation`, err);
            enqueueSnackbar(`Failed to trigger comment automation`, { variant: 'error' });
          }),
        ]);

        mutate();

        enqueueSnackbar('Reply added successfully', { variant: 'success' });

        return result;
      } catch (err) {
        console.log(`Failed to add reply`, err);
        enqueueSnackbar('Failed to add reply', { variant: 'error' });
        throw err;
      }
    },
  );

  let itemCommentsManagerState: ItemCommentsManagerState = {
    state: 'init',
  };

  const currentUserHasMissingPermissions =
    _.difference(mondayContext?.permissions?.requiredScopes || [], mondayContext?.permissions?.approvedScopes || []).length > 0;

  if (mondayContextError) {
    itemCommentsManagerState = {
      state: 'error',
      errorKind: 'MONDAY_CONTEXT',
      error: extractErrorMessage(mondayContextError, 'An error occurred while fetching monday context'),
    };
  } else if (currentUserHasMissingPermissions) {
    itemCommentsManagerState = {
      state: 'error',
      errorKind: 'MISSING_PERMISSIONS',
      error: 'You are missing some permission scopes to use this feature. Please approve the required scopes.',
    };
  } else if (currentUserError) {
    itemCommentsManagerState = {
      state: 'error',
      errorKind: 'ME',
      error: extractErrorMessage(currentUserError, 'An error occurred while fetching monday context'),
    };
  } else if (portalsError) {
    itemCommentsManagerState = {
      state: 'error',
      errorKind: 'LOAD_PORTALS',
      error: extractErrorMessage(portalsError, 'An error occurred while loading portals'),
    };
  }

  if (itemCommentsManagerState.state !== 'error' && mondayContext && currentUser && portalsWithBoardInfo) {
    itemCommentsManagerState = {
      state: 'ready',
    };
  }

  return {
    comments,
    isLoadingComments,
    arePortalsLoading,
    addUpdateTrigger,
    isCreatingUpdate,
    creatingUpdateError,
    itemId: mondayContext?.itemId,
    boardId: mondayContext?.boardId,
    addReplyTrigger,
    creatingReplyError,
    isCreatingReply,
    pageSize,
    page,
    setPage,
    selectedClientItemId,
    setSelectedClientItemId,
    selectedClientItem,
    portalsWithBoardInfo,
    selectedPortalId,
    setSelectedPortalId,
    selectedPortal,
    currentItem,
    currentUser,
    currentUserError,
    commentsError,
    itemCommentsManagerState,
  };
}
